import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import EmailPageShell from './EmailPageShell';
import RequestLinkForm from './RequestLinkForm';
import {
  fetchPreferencesByToken,
  unsubscribeByToken,
  resubscribeByToken,
  submitUnsubscribeFeedback,
} from './emailPreferencesApi';
import styles from './Email.module.css';

const REASONS = [
  { value: 'too_many', label: 'I get too many emails' },
  { value: 'not_relevant', label: "The content isn't relevant to me" },
  { value: 'never_signed_up', label: 'I never signed up for these emails' },
  { value: 'other', label: 'Other' },
];

const labelFor = (meta, key) => (meta || []).find((c) => c.key === key)?.label || 'these emails';

const UnsubscribePage = () => {
  const [params] = useSearchParams();
  const t = params.get('t') || '';

  const [view, setView] = useState(t ? 'loading' : 'invalid');
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [scope, setScope] = useState('all');
  const [reason, setReason] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [feedbackSent, setFeedbackSent] = useState(false);

  const load = useCallback(async () => {
    if (!t) return;
    setView('loading');
    const res = await fetchPreferencesByToken(t, 'unsubscribe');
    if (!res.ok) {
      setError(res.status === 400 ? res.error : 'We could not load this page. Please try again.');
      setView(res.status === 400 ? 'invalid' : 'error');
      return;
    }
    setInfo(res.data);
    setView(res.data.globalOptOut ? 'already' : 'confirm');
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const doUnsubscribe = async (nextScope) => {
    setBusy(true);
    setError('');
    const res = await unsubscribeByToken(t, nextScope);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setInfo(res.data);
    setScope(nextScope);
    setView('done');
  };

  const doResubscribe = async (nextScope) => {
    setBusy(true);
    setError('');
    const res = await resubscribeByToken(t, nextScope);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setInfo(res.data);
    setView('resubscribed');
  };

  const sendFeedback = async (e) => {
    e.preventDefault();
    if (!reason) return;
    setBusy(true);
    await submitUnsubscribeFeedback(t, reason, reason === 'other' ? reasonText.trim() : '');
    setBusy(false);
    setFeedbackSent(true);
  };

  const prefsLink = `/email-preferences?t=${encodeURIComponent(t)}`;
  const masked = info?.maskedEmail;
  const source = info?.sourceCategory;
  const sourceLabel = labelFor(info?.categoryMeta, source);
  const sourceStillOn = source && info?.categories?.[source];

  let content;
  if (view === 'loading') {
    content = <div className={styles.loader} role="status">Loading…</div>;
  } else if (view === 'invalid') {
    content = (
      <>
        <div className={styles.icon} aria-hidden="true">🔗</div>
        <h1 className={styles.title}>This link isn't valid</h1>
        <p className={styles.subtitle}>
          {error || 'The unsubscribe link is incomplete or has been replaced by a newer one.'} Enter your email and we'll send you a
          fresh link to manage your emails.
        </p>
        <RequestLinkForm />
      </>
    );
  } else if (view === 'error') {
    content = (
      <>
        <h1 className={styles.title}>Something went wrong</h1>
        <p className={styles.error} role="alert">{error}</p>
        <div className={styles.actions}>
          <button type="button" className={styles.primaryBtn} onClick={load}>Try again</button>
        </div>
      </>
    );
  } else if (view === 'confirm') {
    content = (
      <>
        <h1 className={styles.title}>Unsubscribe from Bubble Ai emails?</h1>
        <p className={styles.subtitle}>
          You're managing emails for <span className={styles.email}>{masked}</span>.
        </p>
        <div className={styles.actions}>
          {sourceStillOn && (
            <button type="button" className={styles.secondaryBtn} disabled={busy} onClick={() => doUnsubscribe(source)}>
              Only unsubscribe from {sourceLabel}
            </button>
          )}
          <button type="button" className={styles.primaryBtn} disabled={busy} onClick={() => doUnsubscribe('all')}>
            {busy ? 'Unsubscribing…' : 'Unsubscribe from all optional emails'}
          </button>
          <Link to={prefsLink} className={styles.linkBtn}>Choose which emails you get instead</Link>
        </div>
        {error && <p className={styles.error} role="alert">{error}</p>}
        <p className={styles.note}>
          You'll still receive essential emails such as password resets, security alerts, payment receipts and required school notices.
        </p>
      </>
    );
  } else if (view === 'done' || view === 'already') {
    const allOff = view === 'already' || scope === 'all';
    content = (
      <>
        <div className={styles.icon} aria-hidden="true">✅</div>
        <h1 className={styles.title}>{view === 'already' ? "You're already unsubscribed" : "You've been unsubscribed"}</h1>
        <p className={styles.subtitle}>
          {allOff
            ? <><span className={styles.email}>{masked}</span> will no longer receive optional emails from Bubble Ai.</>
            : <><span className={styles.email}>{masked}</span> will no longer receive {sourceLabel.toLowerCase()} emails.</>}
          {' '}It can take a few minutes for messages already on their way to stop.
        </p>
        <div className={styles.actions}>
          {info?.canResubscribe && (
            <button type="button" className={styles.secondaryBtn} disabled={busy} onClick={() => doResubscribe(view === 'already' ? 'all' : scope)}>
              {view === 'already' ? 'Resubscribe' : 'Undo — keep me subscribed'}
            </button>
          )}
          {!allOff && (
            <button type="button" className={styles.secondaryBtn} disabled={busy} onClick={() => doUnsubscribe('all')}>
              Unsubscribe from all optional emails
            </button>
          )}
          <Link to={prefsLink} className={styles.linkBtn}>Manage email preferences</Link>
        </div>
        {error && <p className={styles.error} role="alert">{error}</p>}

        {view === 'done' && (
          <div className={styles.survey}>
            {feedbackSent ? (
              <p className={styles.success} role="status">Thanks for your feedback — it helps us send better emails.</p>
            ) : (
              <form onSubmit={sendFeedback}>
                <p className={styles.surveyTitle}>Mind telling us why? (optional)</p>
                {REASONS.map((r) => (
                  <label key={r.value} className={styles.radio}>
                    <input type="radio" name="reason" value={r.value} checked={reason === r.value} onChange={() => setReason(r.value)} />
                    {r.label}
                  </label>
                ))}
                {reason === 'other' && (
                  <textarea
                    className={styles.textarea}
                    maxLength={500}
                    placeholder="Tell us more (optional)"
                    aria-label="Tell us more"
                    value={reasonText}
                    onChange={(e) => setReasonText(e.target.value)}
                  />
                )}
                <div className={styles.actions}>
                  <button type="submit" className={styles.secondaryBtn} disabled={!reason || busy}>Send feedback</button>
                </div>
              </form>
            )}
          </div>
        )}
      </>
    );
  } else if (view === 'resubscribed') {
    content = (
      <>
        <div className={styles.icon} aria-hidden="true">🎉</div>
        <h1 className={styles.title}>You're subscribed again</h1>
        <p className={styles.subtitle}>
          <span className={styles.email}>{masked}</span> will keep receiving emails from Bubble Ai. You can fine-tune what you get at any time.
        </p>
        <div className={styles.actions}>
          <Link to={prefsLink} className={styles.linkBtn}>Manage email preferences</Link>
        </div>
      </>
    );
  }

  return <EmailPageShell title="Unsubscribe">{content}</EmailPageShell>;
};

export default UnsubscribePage;
